import { AuthenticationError } from '@auth0/auth0-react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { authQueryKey } from '@/api/query-keys';
import { accountService } from '@/api/services/account';
import { iamService } from '@/api/services/iam';
import { quotaService } from '@/api/services/quota';
import { setSentryUser } from '@/helpers/errors/identity';
import { reportError } from '@/helpers/errors/report';
import { enqueueSnackbar } from '@/hooks/use-qdrant-snackbar';
import { resolveSelectedAccountId, resolveStatsigUpdateUser } from '@/router/resolvers/user';
import { authenticatedSearchSchema } from '@/router/utils';
import { UnavailableAuth0Context } from './__root.hooks';
import { AuthenticatedComponent, AuthenticatedErrorFallback } from './_authenticated.component';

export const Route = createFileRoute('/_authenticated')({
  validateSearch: authenticatedSearchSchema,
  beforeLoad: async ({ context: { auth, queryClient }, params, search, location }) => {
    if (auth === UnavailableAuth0Context) {
      // Discriminate type to satisfy the compiler: this case should never happen because the route is only rendered when Auth0 context is available
      throw new Error('Auth context is not available');
    }
    if (auth.error) {
      // From all known cases, 'Forbidden' happens when the refresh token expires.
      const isForbiddenAccessError =
        isAuthenticationError(auth.error) &&
        auth.error.error === 'access_denied' &&
        auth.error.error_description === 'Forbidden';

      if (!isForbiddenAccessError) {
        // Something went wrong on the auth0 side (or network request failed), send to Sentry and logout.
        reportError(auth.error, {
          domain: 'auth',
          action: 'authenticate',
          level: 'fatal',
          extra: {
            error: isAuthenticationError(auth.error) ? auth.error.error : auth.error.message,
            error_description: isAuthenticationError(auth.error) ? auth.error.error_description : undefined,
          },
        });
      }
      // Auth errored out, redirect to logout.
      throw redirect({
        to: '/logout',
        search: { aerr: 'invalid', return_to: location.pathname },
      });
    }

    // If the user is not authenticated, redirect to login.
    if (!auth.isAuthenticated) {
      // Keep the whole query string: the OAuth consent page needs its `session_token` and `state`
      // back after login, as does `cluster_ui_aerr`.
      const returnTo = location.pathname + location.searchStr;
      throw redirect({
        to: '/login',
        replace: true,
        search: { return_to: returnTo },
      });
    }

    if (search.cluster_ui_aerr) {
      if (search.cluster_ui_aerr === 'expired' || search.cluster_ui_aerr === 'invalid') {
        const message = [
          search.cluster_ui_aerr === 'expired'
            ? 'Your Cluster UI access has expired.'
            : 'Your Cluster UI access is invalid.',
          ' Try again using the Cluster UI action in the cluster detail section.',
        ];
        enqueueSnackbar(message.join(''), {
          variant: 'default',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
      }

      throw redirect({
        to: '.',
        replace: true,
        search: (prev) => ({ ...prev, cluster_ui_aerr: undefined }),
      });
    }

    await queryClient.fetchQuery({
      queryKey: authQueryKey,
    });
    const getAuthenticatedUserQuery = iamService.getAuthenticatedUserQuery();
    const listAccountsQuery = accountService.listAccountsQuery();

    const userAccountContext = { queryClient, getAuthenticatedUserQuery, listAccountsQuery };
    const selectedAccountId = resolveSelectedAccountId({ params, search });

    /**
     * idea is to not block beforeload with this hence it is resolved asynchronously. And allow individual routes to self-evaluate
     * by awaiting it in the beforeLoad. Principle is to ensure beforeLoad awaits only what is required by the route itself.
     */
    const statsigUser = resolveStatsigUpdateUser({ accountId: selectedAccountId, ...userAccountContext });

    return {
      /**
       * The 'auth' context should not be used in child routes to prevent misuse. Use the next instead:
       * @see {@link import('./_authenticated.hooks').useAuthenticatedUserClaims}
       */
      auth: UnavailableAuth0Context,
      statsigUser,
      getAuthenticatedUserQuery,
      getUserProfileQuery: iamService.getUserProfileQuery(),
      listAccountsQuery,
      getAuthenticatedUserQuotasQuery: quotaService.getAuthenticatedUserQuotasQuery(),
      listEffectivePermissionsQuery: selectedAccountId
        ? iamService.listEffectivePermissionsQuery({ accountId: selectedAccountId })
        : undefined,
    };
  },
  loader: ({
    context: {
      queryClient,
      statsigUser,
      getUserProfileQuery,
      getAuthenticatedUserQuery,
      listAccountsQuery,
      getAuthenticatedUserQuotasQuery,
      listEffectivePermissionsQuery,
    },
  }) => {
    void queryClient.prefetchQuery(listAccountsQuery);
    void queryClient.prefetchQuery(getAuthenticatedUserQuotasQuery);
    if (listEffectivePermissionsQuery) {
      void queryClient.prefetchQuery(listEffectivePermissionsQuery);
    }
    void queryClient.prefetchQuery(getUserProfileQuery);
    void queryClient.ensureQueryData(getAuthenticatedUserQuery).then(({ user }) => {
      if (user) {
        setSentryUser({ id: user.id });
      }
    });

    /**
     * Ensures that the gate is evaluated and is ready for components that rely on statsig user without blocking
     * the route's loading process.
     */
    void statsigUser.then((query) => queryClient.prefetchQuery(query));
  },
  errorComponent: AuthenticatedErrorFallback,
  component: AuthenticatedComponent,
});

function isAuthenticationError(err: unknown): err is AuthenticationError {
  return err != null && err instanceof AuthenticationError;
}
